import React, { useState, useRef, useEffect, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import type { EventContentArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule';
import Alert from "@amzn/awsui-components-core/alert";
import Box from "@amzn/awsui-components-core/box";
import Button from "@amzn/awsui-components-core/button";
import Checkbox from "@amzn/awsui-components-core/checkbox";
import FormField from "@amzn/awsui-components-core/form-field";
import Grid from "@amzn/awsui-components-core/grid";
import Icon from "@amzn/awsui-components-core/icon";
import Input from "@amzn/awsui-components-core/input";
import Modal from "@amzn/awsui-components-core/modal";
import Multiselect from "@amzn/awsui-components-core/multiselect";
import Select from "@amzn/awsui-components-core/select";
import SpaceBetween from "@amzn/awsui-components-core/space-between";
import Spinner from "@amzn/awsui-components-core/spinner";
import Textarea from "@amzn/awsui-components-core/textarea";
import { EventInput, DateSelectArg, EventClickArg } from '@fullcalendar/core';
import ActionButtons from 'src/components/shared/ActionButtons';
import { useUser } from 'src/context/UserContext';
import { fetchCalendarEvents, createCalendarEvent, updateCalendarEvent, deleteCalendarEvent, CalendarEventEntry } from 'src/services/calendar-api';
import { useAlertMessages } from 'src/utils/useAlertMessages';
import { formatPlainTextToHtml } from 'src/utils/helpers';
import { SERVICE_LINES } from 'src/constants';
import { getDefaultVisibility } from 'src/roles-permissions';
import { greenButtonStyles } from 'src/components/shared/design-tokens';
import { requiredMark, requiredFieldHint } from 'src/components/shared/formHelpers';
import './CalendarSection.scss';
import mockData from 'src/mock/mockData.json';

interface CalendarEvent extends EventInput {
    id: string;
    title: string;
    start: string;
    end?: string;
    description?: string;
    allDay?: boolean;
    eventId?: string;
    rrule?: string;
    duration?: string;
    startRecur?: string;
    endRecur?: string;
    _rawEndRecur?: string;
    targetGroups?: string[];
    createdBy?: string;
    postAnonymously?: boolean;
}

interface PopoverPos { top: number; left: number; openLeft?: boolean; }

// Toggle this to false to use the real API
const USE_MOCK_DATA = false;

const CalendarSection = () => {
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [popoverEvent, setPopoverEvent] = useState<CalendarEvent | null>(null);
    const [popoverPos, setPopoverPos] = useState<PopoverPos | null>(null);
    const [popoverEditing, setPopoverEditing] = useState(false);
    const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [allDay, setAllDay] = useState(false);
    // Admin / Super Author only: hide the creator's alias in the event popover.
    const [postAnonymously, setPostAnonymously] = useState(false);
    const [recurrenceRule, setRecurrenceRule] = useState('none');
    const [recurrenceDays, setRecurrenceDays] = useState<number[]>([]);
    const [endRecur, setEndRecur] = useState('');
    const [targetGroups, setTargetGroups] = useState<string[]>([]);
    const { error, successMsg, setError, setSuccessMsg, alertRef } = useAlertMessages();
    const [submitting, setSubmitting] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
    const calendarRef = useRef<FullCalendar>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const sectionRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const POPOVER_MIN_WIDTH = 320;
    const { user, canWrite, canModify, hasRole } = useUser();
    const canEdit = canWrite('calendar');
    // Calendar events can target any service line — supervisors (and above) may
    // add events for all groups, so no options are disabled here.
    // const allowedVisibility = getAllowedVisibility(user?.permissions || []);
    const visibilityOptions = SERVICE_LINES
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(opt => ({
                label: opt.name,
                value: opt.value,
                // disabled: allowedVisibility.length > 0 && !allowedVisibility.includes(opt.value),
            }));

    // Load events from API
    useEffect(() => {
        loadEvents();
    }, []);

    const loadEvents = async () => {
        setLoading(true);
        try {
            if (USE_MOCK_DATA) {
                setEvents(mockData.calendar.items.map(item => ({
                    id: item.eventId,
                    eventId: item.eventId,
                    title: item.title,
                    start: item.start,
                    end: item.end || undefined,
                    description: item.description,
                    allDay: item.allDay,
                    rrule: (item as any).rrule || undefined,
                    duration: (item as any).duration || undefined,
                    targetGroups: (item as any).targetGroups || [],
                    startRecur: (item as any).startRecur || '',
                    endRecur: (item as any).endRecur || '',
                    _rawEndRecur: (item as any).endRecur || '',  // preserve original for edit form
                    postAnonymously: (item as any).postAnonymously || false,
                    createdBy: (item as any).createdBy,
                })));
            } else {
                const data = await fetchCalendarEvents();
                setEvents(data.items.map(item => ({
                    id: item.eventId,
                    eventId: item.eventId,
                    title: item.title,
                    start: item.start,
                    end: item.end || undefined,
                    description: item.description,
                    allDay: item.allDay,
                    rrule: (item as any).rrule || undefined,
                    duration: (item as any).duration || undefined,
                    targetGroups: (item as any).targetGroups || [],
                    startRecur: (item as any).startRecur || '',
                    endRecur: (item as any).endRecur || '',
                    _rawEndRecur: (item as any).endRecur || '',  // preserve original for edit form
                    postAnonymously: (item as any).postAnonymously || false,
                    createdBy: item.createdBy,
                })));
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load events');
        } finally {
            setLoading(false);
        }
    };

    const closePopover = () => {
        setPopoverEvent(null);
        setPopoverPos(null);
        setPopoverEditing(false);
        setEditingEvent(null);
        setTitle('');
        setDescription('');
        setStartDate('');
        setEndDate('');
        setAllDay(false);
        setRecurrenceRule('none');
        setRecurrenceDays([]);
        setEndRecur('');
        setTargetGroups([]);
        setPostAnonymously(false);
        setError(null);
    };

    const resetTopForm = () => {
        setShowForm(false);
        setEditingEvent(null);
        setTitle('');
        setDescription('');
        setStartDate('');
        setEndDate('');
        setAllDay(false);
        setRecurrenceRule('none');
        setRecurrenceDays([]);
        setEndRecur('');
        setTargetGroups([]);
        setPostAnonymously(false);
        setError(null);
    };

    const handleOutsideClick = useCallback((e: MouseEvent) => {
        if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
            closePopover();
        }
    }, []);

    useEffect(() => {
        if (popoverEvent) {
            document.addEventListener('mousedown', handleOutsideClick);
        }
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [popoverEvent, handleOutsideClick]);

    const handleDateSelect = (selectInfo: DateSelectArg) => {
        closePopover();
        setStartDate(selectInfo.startStr);
        setEndDate(selectInfo.endStr);
        setAllDay(selectInfo.allDay);
        setEditingEvent(null);
        setShowForm(true);
    };

    const handleEventClick = (clickInfo: EventClickArg) => {
        const ev = events.find(e => e.id === clickInfo.event.id);
        if (!ev) return;
        resetTopForm();
        const wrapperRect = wrapperRef.current?.getBoundingClientRect();
        const elRect = clickInfo.el.getBoundingClientRect();
        if (wrapperRect) {
            const spaceOnRight = wrapperRect.right - elRect.left;
            const openLeft = spaceOnRight < POPOVER_MIN_WIDTH;

            setPopoverPos({
                top: elRect.bottom - wrapperRect.top + 2,
                left: openLeft
                    ? elRect.right - wrapperRect.left // anchor to event's right edge
                    : elRect.left - wrapperRect.left, // anchor to event's left edge
                openLeft
            });
        }
        setPopoverEvent(ev);
        setPopoverEditing(false);
    };

    const startPopoverEdit = () => {
        if (!popoverEvent) return;
        const isAllDay = popoverEvent.allDay || false;
        setEditingEvent(popoverEvent);
        setTitle(popoverEvent.title);
        setDescription(popoverEvent.description || '');
        // All-day events use plain date strings; timed events convert from UTC.
        setStartDate(isAllDay ? (popoverEvent.start || '').slice(0, 10) : toLocal(popoverEvent.start));
        setEndDate(popoverEvent.end ? (isAllDay ? popoverEvent.end.slice(0, 10) : toLocal(popoverEvent.end)) : '');
        setAllDay(isAllDay);
        // Load existing recurrence if present
        if (popoverEvent.rrule) {
            const rrule = popoverEvent.rrule;
            if (rrule.includes('FREQ=DAILY')) setRecurrenceRule('daily');
            else if (rrule.includes('INTERVAL=2')) setRecurrenceRule('biweekly');
            else if (rrule.includes('FREQ=WEEKLY')) setRecurrenceRule('weekly');
            else if (rrule.includes('FREQ=MONTHLY')) setRecurrenceRule('monthly');
            else if (rrule.includes('FREQ=YEARLY')) setRecurrenceRule('yearly');
            // Parse BYDAY
            const byDayMatch = rrule.match(/BYDAY=([A-Z,]+)/);
            if (byDayMatch) {
                const days = byDayMatch[1].split(',').map(d => DAY_NAMES.indexOf(d)).filter(d => d >= 0);
                setRecurrenceDays(days);
            }
        } else {
            setRecurrenceRule('none');
            setRecurrenceDays([]);
        }
        setTargetGroups(popoverEvent.targetGroups || []);
        setPostAnonymously(popoverEvent.postAnonymously || false);
        setEndRecur(popoverEvent._rawEndRecur || popoverEvent.endRecur || '');
        setPopoverEditing(true);
    };

    const handleDelete = () => {
        const ev = popoverEvent || editingEvent;
        if (!ev) return;
        setDeleteTarget({ id: ev.id, title: ev.title });
        closePopover();
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteCalendarEvent(deleteTarget.id);
            setEvents(prev => prev.filter(e => e.id !== deleteTarget.id));
            setSuccessMsg('Event deleted');
        } catch (err: any) {
            setError(err.message || 'Failed to delete event');
        }
        setDeleteTarget(null);
    };

    const handleSave = async () => {
        if (!title.trim()) { setError('Title is required'); return; }
        if (!startDate) { setError('Start date is required'); return; }

        const rruleStr = recurrenceRule !== 'none'
            ? buildRRule(recurrenceRule, startDate, recurrenceDays, endRecur)
            : undefined;

        // All-day events are stored as plain date strings (YYYY-MM-DD) so they
        // are not shifted by timezone conversion. Timed events use UTC.
        const startVal = allDay ? startDate.slice(0, 10) : toUTC(startDate);
        const endVal = endDate ? (allDay ? endDate.slice(0, 10) : toUTC(endDate)) : undefined;

        setSubmitting(true);
        try {
            if (editingEvent) {
                await updateCalendarEvent(editingEvent.id, {
                    title, description, start: startVal, end: endVal, allDay, postAnonymously,
                    targetGroups: targetGroups.length > 0 ? targetGroups : undefined,
                    rrule: rruleStr || '',
                    duration: rruleStr ? calcDuration(startDate, endDate, allDay) : '',
                    startRecur: rruleStr ? startDate.split('T')[0] : '',
                    endRecur: rruleStr ? endRecur || undefined : '',
                });
                setEvents(prev => prev.map(evt =>
                    evt.id === editingEvent.id
                        ? { ...evt, title, description, start: startVal, end: endVal, allDay, postAnonymously, targetGroups, rrule: rruleStr, duration: rruleStr ? calcDuration(startDate, endDate, allDay) : undefined, startRecur: rruleStr ? startDate.split('T')[0] : '', endRecur: rruleStr ? endRecur : '', _rawEndRecur: rruleStr ? endRecur : '' }
                        : evt
                ));
            } else {
                const created = await createCalendarEvent({
                    title, description, start: startVal, end: endVal, allDay, postAnonymously,
                    targetGroups: targetGroups.length > 0 ? targetGroups : undefined,
                    rrule: rruleStr,
                    duration: rruleStr ? calcDuration(startDate, endDate, allDay) : undefined,
                    startRecur: rruleStr ? startDate.split('T')[0] : undefined,
                    endRecur: rruleStr ? endRecur || undefined : undefined,
                });
                setEvents(prev => [...prev, {
                    id: created.eventId,
                    eventId: created.eventId,
                    title: created.title,
                    start: startVal,
                    end: endVal,
                    description: created.description,
                    allDay: created.allDay,
                    rrule: rruleStr,
                    duration: rruleStr ? calcDuration(startDate, endDate, allDay) : undefined,
                    targetGroups: targetGroups,
                    startRecur: rruleStr ? startDate.split('T')[0] : '',
                    endRecur: rruleStr ? endRecur : '',
                    _rawEndRecur: rruleStr ? endRecur : '',
                    postAnonymously: created.postAnonymously,
                    createdBy: created.createdBy,
                }]);
            }
            setSuccessMsg(editingEvent ? 'Event updated' : 'Event created');
            if (popoverEvent) closePopover();
            else resetTopForm();
        } catch (err: any) {
            setError(err.message || 'Failed to save event');
        } finally {
            setSubmitting(false);
        }
    };

    const formatTime = (dateStr: string) => {
        try {
            return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch {
            return dateStr;
        }
    };

    // Format a plain YYYY-MM-DD date without timezone shift (used for all-day events).
    const formatDate = (dateStr: string) => {
        try {
            const d = new Date(`${(dateStr || '').slice(0, 10)}T00:00:00Z`);
            return d.toLocaleDateString([], { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
        } catch {
            return dateStr;
        }
    };

    // Format a timed event value as date + time (e.g. "May 11, 8:00 PM").
    const formatDateTime = (dateStr: string) => {
        try {
            return new Date(dateStr).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        } catch {
            return dateStr;
        }
    };

    // Whether two timed values fall on the same local calendar day.
    const isSameLocalDay = (a: string, b: string): boolean => {
        try {
            return new Date(a).toDateString() === new Date(b).toDateString();
        } catch {
            return false;
        }
    };

    const DAY_NAMES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
    const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const RECUR_OPTIONS = [  
        {
            "label": "Does not repeat",
            "value": "none"
        },
        {
            "label": "Daily",
            "value": "daily"
        },
        {
            "label": "Weekly",
            "value": "weekly"
        },
        {
            "label": "Every 2 weeks",
            "value": "biweekly"
        },
        {
            "label": "Monthly",
            "value": "monthly"
        },
        {
            "label": "Yearly",
            "value": "yearly"
        },
    ]

    // Convert local datetime-local value to UTC ISO string for storage
    const toUTC = (localStr: string): string => {
        if (!localStr) return '';
        return new Date(localStr).toISOString();
    };

    // Convert UTC ISO string to local datetime-local format for form inputs
    const toLocal = (utcStr: string): string => {
        if (!utcStr) return '';
        const d = new Date(utcStr);
        const offset = d.getTimezoneOffset();
        const local = new Date(d.getTime() - offset * 60000);
        return local.toISOString().slice(0, 16);
    };

    // FullCalendar's endRecur is exclusive; add 1 day to make user input inclusive
    const makeEndRecurInclusive = (dateStr: string): string => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        d.setDate(d.getDate() + 1);
        return d.toISOString().split('T')[0];
    };

    // All-day events use an exclusive end in FullCalendar. The user picks an
    // inclusive end date, so add one day so the final day is actually rendered.
    // Works purely on the YYYY-MM-DD date part (UTC math) to avoid timezone drift.
    const makeAllDayEndExclusive = (dateStr: string): string => {
        if (!dateStr) return dateStr;
        const d = new Date(`${dateStr.slice(0, 10)}T00:00:00Z`);
        d.setUTCDate(d.getUTCDate() + 1);
        return d.toISOString().slice(0, 10);
    };

    const buildRRule = (rule: string, dtstart: string, days: number[], until?: string): string => {
        // Convert local datetime to UTC for DTSTART (rrule plugin interprets as UTC)
        const localDate = new Date(dtstart);
        const utcStr = localDate.toISOString().replace(/[-:]/g, '').split('.')[0];
        let dt = utcStr;
        if (dt.includes('T') && dt.length === 13) dt += '00'; // add seconds if YYYYMMDDTHHM -> YYYYMMDDTHHMM00
        if (dt.includes('T') && dt.length === 11) dt += '0000'; // YYYYMMDDT HH -> YYYYMMDDTHH0000
        if (!dt.includes('T')) dt += 'T000000'; // date only
        if (dt.length < 15) dt = dt.padEnd(15, '0'); // ensure full length
        const base = `DTSTART:${dt}Z\nRRULE:`;
        const byDay = days.length > 0 ? `;BYDAY=${days.map(d => DAY_NAMES[d]).join(',')}` : '';
        const untilStr = until ? `;UNTIL=${until.replace(/[-]/g, '')}T235959Z` : '';
        switch (rule) {
            case 'daily': return base + 'FREQ=DAILY' + untilStr;
            case 'weekly': return base + 'FREQ=WEEKLY' + byDay + untilStr;
            case 'biweekly': return base + 'FREQ=WEEKLY;INTERVAL=2' + byDay + untilStr;
            case 'monthly': return base + 'FREQ=MONTHLY' + untilStr;
            case 'yearly': return base + 'FREQ=YEARLY' + untilStr;
            default: return '';
        }
    };

    const toggleDay = (day: number) => {
        setRecurrenceDays(prev =>
            prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort()
        );
    };

    // Form is valid when all visible required inputs are filled
    const isFormValid = (): boolean => {
        if (!title.trim()) return false;
        if (targetGroups.length === 0) return false;
        if (!startDate) return false;
        // If end date is enabled (not allDay and no recurrence), require it
        if (!allDay && recurrenceRule === 'none' && !endDate) return false;
        // If recurrence is set, require an end date for the recurrence
        if (recurrenceRule !== 'none' && !endRecur) return false;
        // If weekly/biweekly recurrence, require at least one day selected
        if ((recurrenceRule === 'weekly' || recurrenceRule === 'biweekly') && recurrenceDays.length === 0) return false;
        return true;
    };

    // Calculate duration for recurring events (HH:MM format for FullCalendar)
    const calcDuration = (start: string, end: string, isAllDay: boolean): string | undefined => {
        if (!start) return undefined;
        if (isAllDay) return undefined; // all-day recurring events don't need duration — FullCalendar defaults to 1 day
        if (end) {
            const diffMs = new Date(end).getTime() - new Date(start).getTime();
            if (diffMs > 0) {
                const hours = Math.floor(diffMs / 3600000);
                const minutes = Math.floor((diffMs % 3600000) / 60000);
                return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
            }
        }
        return '01:00'; // fallback for timed recurring events without end
    };

    const renderEventForm = (onCancel: () => void, submitLabel: string, compact = false) => {
        const showDays = recurrenceRule === 'weekly' || recurrenceRule === 'biweekly';
        // Compact (popover update): stack fields vertically with SpaceBetween.
        // Inline (create): lay fields out in rows with Grid.
        const DateFieldsContainer: React.ElementType = compact ? SpaceBetween : Grid;
        const containerProps: any = compact
            ? { size: 'm' }
            : {
                gridDefinition: showDays
                    ? [
                        // row 1 = All day(2) + Start(5) + End(5)
                        { colspan: 2 }, { colspan: 5 }, { colspan: 5 },
                        // row 2 with day picker = Repeat(4) + Days(3) + Repeat until(5)
                        { colspan: 4 }, { colspan: 4 }, { colspan: 4 },
                    ]
                    : [
                        // row 1 = All day(2) + Start(5) + End(5)
                        { colspan: 2 }, { colspan: 5 }, { colspan: 5 },
                        // row 2 without day picker = Repeat(6) + Repeat until(6)
                        { colspan: 6 }, { colspan: 6 },
                    ],
            };

        return (
        <>
            { error && 
                <Alert 
                    dismissible
                    type="error"
                    onDismiss={() => setError(null)}>{error}
                </Alert>
            }

            <FormField label={<>Title{requiredMark()}</>} stretch>
                <Input 
                    placeholder="Event title" 
                    value={title} 
                    onChange={({ detail }) => setTitle(detail.value)} 
                />
            </FormField>

            <FormField label="Description" stretch>
                <Textarea
                    placeholder="Event description..."
                    value={description}
                    onChange={({ detail }) => setDescription(detail.value)}
                    
                />
            </FormField>

            <div className="calendar-modal__visibility-row">
                <FormField label={<>Visibility{requiredMark()}</>} stretch>
                    <Multiselect
                        placeholder="Select Visibility"
                        options={visibilityOptions}
                        selectedOptions={visibilityOptions.filter(o => targetGroups.includes(o.value))}
                        onChange={({ detail }) => { 
                            setTargetGroups(detail.selectedOptions.map(obj => obj.value as string)); 
                        }}
                        enableSelectAll
                        i18nStrings={{
                            selectAllText: "Select all"
                        }}
                    />
                </FormField>
                {/* New Event form: Post anonymously shares the Visibility line.
                    In the edit popup (compact) it moves next to "All day event". */}
                {!compact && hasRole('admin', 'super-author') && (
                    <div className="calendar-modal__anonymous">
                        <Checkbox
                            data-testid="anonymous-checkbox"
                            onChange={({ detail }) => setPostAnonymously(detail.checked)}
                            checked={postAnonymously}
                        >
                            Post anonymously
                        </Checkbox>
                    </div>
                )}
            </div>

            <div className={`calendar-modal__date-grid ${compact ? 'calendar-modal__date-grid--stacked' : ''}`}>    
                <DateFieldsContainer {...containerProps}>
                    {/* Row 1 */}
                    <div className="calendar-modal__allday">
                        {compact && hasRole('admin', 'super-author') && (
                            <Checkbox
                                data-testid="anonymous-checkbox"
                                onChange={({ detail }) => setPostAnonymously(detail.checked)}
                                checked={postAnonymously}
                            >
                                Post anonymously
                            </Checkbox>
                        )}
                        <Checkbox
                            onChange={({ detail }) => { 
                                setAllDay(detail.checked); 
                                if (detail.checked) {
                                    // All-day events may span multiple days; keep the end date
                                    // but drop the time component from both inputs.
                                    setStartDate(prev => prev ? prev.slice(0, 10) : prev);
                                    setEndDate(prev => prev ? prev.slice(0, 10) : prev);
                                } else {
                                    // Restore a time component so the datetime-local inputs stay valid
                                    setStartDate(prev => prev && !prev.includes('T') ? `${prev}T00:00` : prev);
                                    setEndDate(prev => prev && !prev.includes('T') ? `${prev}T00:00` : prev);
                                }
                            }} 
                            checked={allDay}
                        >
                            All day event
                        </Checkbox>
                    </div>
                    <div className="calendar-modal__date-cell">
                        <FormField label={<>Start{requiredMark()}</>} />
                        <input 
                            type={allDay ? 'date' : 'datetime-local'}
                            className="calendar-modal__date-input" 
                            value={allDay ? startDate.slice(0, 10) : startDate}
                            onChange={(e) => setStartDate(e.target.value)} 
                        />
                    </div>
                    <div className="calendar-modal__date-cell">
                        <FormField label={<>End{(!allDay && recurrenceRule === 'none') ? requiredMark() : null}</>} />
                        <input 
                            type={allDay ? 'date' : 'datetime-local'}
                            className="calendar-modal__date-input" 
                            value={allDay ? endDate.slice(0, 10) : endDate}
                            onChange={(e) => setEndDate(e.target.value)} 
                            disabled={recurrenceRule !== 'none'} 
                        />
                    </div>
                    {/* Row 2 */}
                    <div className="calendar-modal__date-cell">
                        <FormField label="Repeat">
                            {/* <span className="calendar-modal__select"> */}
                            <Select
                                options={RECUR_OPTIONS}
                                selectedOption={RECUR_OPTIONS.filter(o => o.value === recurrenceRule)[0] || null}
                                onChange={({ detail }) => { 
                                    setRecurrenceRule(detail.selectedOption.value || 'none'); 
                                    if (detail.selectedOption.value === 'none') setRecurrenceDays([]);
                                }}
                            />
                            {/* </span> */}
                        </FormField>
                
                    </div>
                    {/* Row 3 */}
                    {(recurrenceRule === 'weekly' || recurrenceRule === 'biweekly') && (
                        <div className="calendar-modal__date-cell calendar-modal__days-cell">
                            <div className="calendar-modal__days-inner">
                                <FormField label={<>Repeat on{requiredMark()}</>} />
                                <div className="calendar-modal__days">
                                    {DAY_LABELS.map((label, idx) => (
                                        <button
                                            type="button"
                                            key={idx}
                                            className={`calendar-modal__day-btn ${recurrenceDays.includes(idx) ? 'selected' : ''}`}
                                            onClick={() => toggleDay(idx)}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="calendar-modal__date-cell">
                        <FormField label={<>Repeat until{recurrenceRule !== 'none' ? requiredMark() : null}</>} />
                        <input
                            type="date"
                            className="calendar-modal__date-input"
                            value={endRecur}
                            onChange={(e) => setEndRecur(e.target.value)}
                            disabled={recurrenceRule === 'none'}
                        />
                    </div>  
                </DateFieldsContainer>
            </div>

            <div className="calendar-modal__actions">
                {requiredFieldHint()}
                <div className="calendar-modal__actions-buttons">
                    <Button 
                        variant="link" 
                        onClick={onCancel} 
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={submitting || !isFormValid()}
                    >
                        {editingEvent ? 'Update' : 'Submit'}
                    </Button>
                </div>
            </div>
        </>
        );
    };

    return (
        <div className="section-card calendar-section" ref={sectionRef}>
            <div className="section-card__header">
                <h3 className="section-card__title">Calendar</h3>
                { canEdit && 
                    <Button 
                        variant="primary" 
                        style={greenButtonStyles}
                        onClick={() => { 
                             closePopover();
                            resetTopForm(); 
                            setTargetGroups(getDefaultVisibility(user?.permissions || []));
                            setShowForm(true);
                        }} 
                    > 
                        New Event
                    </Button>
                }
            </div>

            <div ref={alertRef} tabIndex={-1}>
                {error && !showForm && !popoverEditing && (
                    <div className="alert">
                        <Alert 
                            dismissible
                            type="error"
                            onDismiss={() => setError(null)}
                        >
                            <strong>Error: </strong>{error}
                        </Alert>
                    </div>
                )}
                {successMsg && 
                    <div className="alert">
                        <Alert
                            dismissible
                            type="success"
                            onDismiss={() => setSuccessMsg(null)}
                        >
                            {successMsg}
                        </Alert>
                    </div>
                }
            </div>

            {loading && <Spinner />}

            {/* Top form for new events */}
            {showForm && !popoverEvent && (
                <div className="calendar-modal">
                    <h5 className="calendar-modal__title">New Event</h5>
                    {renderEventForm(resetTopForm, 'Save')}
                </div>
            )}

            <div className="calendar-wrapper" ref={wrapperRef}>
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, rrulePlugin]}
                    initialView="dayGridMonth"
                    headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek,timeGridDay',
                    }}
                    slotLabelFormat={{
                        hour: 'numeric',
                        minute: '2-digit',
                        omitZeroMinute: true,  // shows "12 AM" instead of "12:00 AM"
                        hour12: true
                    }}
                    views={{
                        timeGrid: { displayEventTime: false } // hides time on week & day view
                    }}
                    events={ events
                            .filter(evt => !evt.rrule || evt.rrule.match(/DTSTART:\d{8}T\d{6}Z?/))
                            .map(evt => evt.endRecur ? { ...evt, endRecur: makeEndRecurInclusive(evt.endRecur) } : evt)
                            .map(evt => {
                                // All-day events: render as plain dates so no timezone shift
                                // occurs, and expand the (inclusive) end to FullCalendar's
                                // exclusive end so the final day shows.
                                if (!evt.allDay) return evt;
                                const start = (evt.start || '').slice(0, 10);
                                const end = (evt.end && !evt.rrule) ? makeAllDayEndExclusive(evt.end) : evt.end;
                                return { ...evt, start, end };
                            })
                    }
                    selectable={false}
                    selectMirror={true}
                    editable={false}
                    select={handleDateSelect}
                    eventClick={handleEventClick}
                    eventTimeFormat={{
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                    }}
                    // Tag all-day events so CSS can prepend a bold "All Day" label where
                    // timed events show their time. Timed events keep FullCalendar's default
                    // rendering (time + title on one line).
                    eventClassNames={(arg: EventContentArg) =>
                        arg.event.allDay ? ['calendar-event--allday'] : []
                    }
                    height="auto"
                />

                {/* Popover */}
                {popoverEvent && popoverPos && (
                    <div
                        className={`calendar-popover${popoverEditing ? " calendar-popover--editing" : ""}`}
                        ref={popoverRef}
                        style={{ top: popoverPos.top, left: popoverPos.left, ...(popoverPos.openLeft && { transform: 'translateX(-100%)' }) }}>

                        {!popoverEditing ? (
                            <>
                                <div className="calendar-popover__header">
                                    <strong className="calendar-popover__title">{popoverEvent.title}</strong>
                                    <div className="calendar-popover__actions">
                                        {canModify('calendar', popoverEvent.createdBy) && (
                                            <ActionButtons
                                                onEdit={startPopoverEdit}
                                                onDelete={handleDelete}
                                                disabled={showForm}
                                            />
                                        )}
                                        <Button
                                            variant="icon"
                                            iconName="close" 
                                            onClick={closePopover}
                                        />
                                    </div>
                                </div>
                                {popoverEvent.description && (
                                    <p className="calendar-popover__desc"
                                        dangerouslySetInnerHTML={{ __html: formatPlainTextToHtml(popoverEvent.description) }}
                                    />
                                )}
                                <div className="calendar-popover__detail">
                                    <Icon name="status-pending" size="x-small" />
                                    {popoverEvent.allDay ? (
                                        <span>
                                            {formatDate(popoverEvent.start)}
                                            {popoverEvent.end && popoverEvent.end.slice(0, 10) !== popoverEvent.start.slice(0, 10) && (
                                                <> - {formatDate(popoverEvent.end)}</>
                                            )}
                                        </span>
                                    ) : (
                                        <>
                                            <span>{formatDateTime(popoverEvent.start)}</span>
                                            {popoverEvent.end && (
                                                <span> - {isSameLocalDay(popoverEvent.start, popoverEvent.end)
                                                    ? formatTime(popoverEvent.end)
                                                    : formatDateTime(popoverEvent.end)}</span>
                                            )}
                                        </>
                                    )}
                                </div>
                                {popoverEvent.createdBy && !popoverEvent.postAnonymously && (
                                    <div className="calendar-popover__detail">
                                        <Icon name="user-profile" size="x-small" />
                                        <span>Created by: {popoverEvent.createdBy}</span>
                                    </div>
                                )}
                                {(popoverEvent.allDay || (popoverEvent.targetGroups && popoverEvent.targetGroups.length > 0)) && (
                                    <div className="calendar-popover__tags">
                                        {popoverEvent.allDay && (
                                            <span className="calendar-popover__badge calendar-popover__badge--allday">ALL DAY</span>
                                        )}
                                        {((popoverEvent.targetGroups || []).includes('all')
                                            ? SERVICE_LINES.map(s => s.value)
                                            : (popoverEvent.targetGroups || [])
                                        ).map((tag, i) => (
                                            <span key={i} className="calendar-popover__badge">
                                                {tag?.toUpperCase()}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="calendar-popover__header">
                                    <strong className="calendar-popover__title">Edit Event</strong>
                                    <Button
                                        variant="icon"
                                        iconName="close" 
                                        onClick={closePopover}
                                    />
                                </div>
                                {renderEventForm(closePopover, 'Update', true)}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Delete confirmation modal */}
            {deleteTarget && (
                <Modal 
                    visible 
                    header="Delete Event"
                    onDismiss={() => setDeleteTarget(null)}
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                                <Button variant="primary" onClick={confirmDelete}>Delete</Button>
                            </SpaceBetween>
                        </Box>

                    }
                >
                    Are you sure you want to delete <strong>"{deleteTarget.title}"</strong>? This cannot be undone.
                </Modal>
            )}
        </div>
    );
};

export default CalendarSection;
